/// <amd-module name="CodeCoverage/CodeCoverageCiTab/Services/CoverageTabProviderServiceBase" />
import * as React from "react";

import { getTFSProject } from "Tfs/Platform/Page";
import { IObservableValue, ObservableValue } from "VSS/Core/Observable";
import { equals, format } from "VSS/Core/Util/String";
import { IVssPageContext, VssService } from "VSS/Platform/Context";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { WrappedComponent } from "VSS/Platform/Layout";
import { routeUrl } from "VSS/Platform/Location";
import { minute } from "VSSUI/Utilities/Date";

import { BuildArtifact, BuildStatus } from "Build/Client/Contracts/Build";
import { getBuildClient } from "Build/Client/RestClient/Build";
import { PipelineCoverageSummary } from "CodeCoverage/Clients/Coverage/Contracts/Coverage";
import { IRepoCodeCoverageProps, RepoCodeCoverage } from "CodeCoverage/CodeCoverageCiTab/Components/RepoCodeCoverage";
import { CoverageSummaryPoller } from "CodeCoverage/CodeCoverageCiTab/CoverageSummaryPoller";
import { ICodeCoverageDataService } from "CodeCoverage/Common/Interfaces/ICodeCoverageDataService";
import { ICodeCoverageTimelineSummaryData } from "CodeCoverage/Common/Interfaces/ICodeCoverageTimelineSummaryData";
import { PathConstants } from "CodeCoverage/ViewCommon/Constants";
import { CoverageEvaluationStatus } from "CodeCoverage/ViewCommon/Contracts/Common";
import { CodeCoverageDataProviderContract } from "CodeCoverage/ViewCommon/Contracts/DataProvider";
import { RunViewArtifact } from "CodeCoverage/ViewCommon/RunViewArtifact";

export abstract class CoverageTabProviderServiceBase extends VssService {
    private latestCoverageData: CodeCoverageDataProviderContract;
    private coveragePoller: CoverageSummaryPoller;
    private tabInitialized: boolean;
    private runViewArtifact: RunViewArtifact;

    private htmlSummaryLink: IObservableValue<string | undefined>;
    private pipelineCoverageSummary: IObservableValue<PipelineCoverageSummary>;
    private contributionService: IVssContributionService;

    protected abstract initTab: (getTab: () => JSX.Element) => void;

    public _serviceStart(pageContext: IVssPageContext): void {
        super._serviceStart(pageContext);

        this.htmlSummaryLink = new ObservableValue(undefined);
    }

    protected startFetchTab(runViewArtifact: RunViewArtifact) {
        this.tabInitialized = false;
        this.runViewArtifact = runViewArtifact;

        if (!this.coveragePoller) {
            this.coveragePoller = new CoverageSummaryPoller(this.pageContext, this.summaryCallback, {
                interval: 10000,
                limitCount: 30,
                projectId: this.runViewArtifact.projectId,
                pipelineRunId: this.runViewArtifact.buildId
            });
        }

        this.contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");

        const dataProvider = "ms.vss-codecoverage-ci-web.code-coverage-ci-data-provider";

        this.latestCoverageData = this.contributionService.getData<CodeCoverageDataProviderContract>(
            dataProvider
        ) as CodeCoverageDataProviderContract;

        if (!this.latestCoverageData) {
            this.contributionService
                .getDataAsync<CodeCoverageDataProviderContract>(dataProvider)
                .then((response: CodeCoverageDataProviderContract | undefined) => {
                    if (response) {
                        this.latestCoverageData = response;
                        this.updateTabAndPollData();
                    }
                });
        } else {
            this.updateTabAndPollData();
        }
    }

    private updateTabAndPollData(): void {
        const currentDate = new Date();
        const diff_minutes = this.runViewArtifact.finishTime ? (currentDate.getTime() - this.runViewArtifact.finishTime.getTime()) / minute : -1;

        if (
            diff_minutes >= this.latestCoverageData.buildDurationCheckTimeInMinutes &&
            (!this.latestCoverageData.pipelineCoverageSummary || !this.latestCoverageData.pipelineCoverageSummary.summary)
        ) {
            this.initializeOldTabContent();
        } else {
            this.updateTab();
            if (this.shouldPollForData()) {
                this.coveragePoller.startPolling();
            }
        }
    }

    private initializeOldTabContent(): void {
        this.contributionService
            .getService<ICodeCoverageDataService>("ms.vss-codecoverage-web.codecoverage-data-service")
            .then((codeCoverageDataService: ICodeCoverageDataService | undefined) => {
                if (codeCoverageDataService) {
                    codeCoverageDataService
                        .getCodeCoverageData(this.runViewArtifact.buildId, this.runViewArtifact.projectId)
                        .then((summary: ICodeCoverageTimelineSummaryData[] | undefined) => {
                            if (summary) {
                                this.initializeTab(true);
                            }
                        });
                }
            });
    }

    private summaryCallback = (summary: PipelineCoverageSummary) => {
        this.latestCoverageData.pipelineCoverageSummary = summary;

        if (!this.shouldPollForData()) {
            this.coveragePoller.stopPolling();
        }

        this.updateTab();
    };

    private updateTab(): void {
        const summary = this.latestCoverageData.pipelineCoverageSummary;

        if (summary) {
            if (!this.pipelineCoverageSummary) {
                this.pipelineCoverageSummary = new ObservableValue(summary);
            } else if (this.pipelineCoverageSummary.value.evaluationStatus !== summary.evaluationStatus) {
                this.pipelineCoverageSummary.value = summary;
            }

            if (
                summary.evaluationStatus === CoverageEvaluationStatus.InProgress ||
                summary.evaluationStatus === CoverageEvaluationStatus.Finalized ||
                summary.evaluationStatus === CoverageEvaluationStatus.FinalizedWithErrors
            ) {
                this.initializeTab();
            } else if (summary.evaluationStatus === CoverageEvaluationStatus.None) {
                this.initHtmlReport();
            }
        }
    }

    private initHtmlReport(): void {
        if (this.htmlSummaryLink.value) {
            this.initializeTab();
        } else {
            // check for html reports
            this.fetchCodeCoverageHTMLSummary(this.runViewArtifact).then(url => {
                if (url) {
                    this.htmlSummaryLink.value = url;
                    this.initializeTab();
                }
            });
        }
    }

    private initializeTab(isOldTab?: boolean): void {
        if (!this.tabInitialized) {
            this.initTab(() => {
                if (!isOldTab) {
                    return React.createElement(RepoCodeCoverage, {
                        runViewArtifact: this.runViewArtifact,
                        changedFiles: this.latestCoverageData.changedFiles,
                        pipelineCoverageSummary: this.pipelineCoverageSummary,
                        htmlSummaryLink: this.htmlSummaryLink
                    } as IRepoCodeCoverageProps);
                } else {
                    return React.createElement(WrappedComponent, {
                        dependencies: ["ms.vss-codecoverage-web.codecoverage-tab"],
                        wrappedType: "CC.BuildCodeCoverage",
                        runViewArtifact: {
                            buildId: this.runViewArtifact.buildId,
                            projectId: this.runViewArtifact.projectId,
                            buildStatus: BuildStatus.Completed
                        }
                    });
                }
            });

            this.tabInitialized = true;
        }
    }

    private shouldPollForData(): boolean {
        if (!this.latestCoverageData.pipelineCoverageSummary) {
            return true;
        }

        switch (this.latestCoverageData.pipelineCoverageSummary.evaluationStatus) {
            case CoverageEvaluationStatus.None:
                return false;

            case CoverageEvaluationStatus.InProgress:
                return true;

            case CoverageEvaluationStatus.FinalizedWithErrors:
            case CoverageEvaluationStatus.Finalized:
                return false;

            case "":
            case null:
            case undefined:
                return true;

            default:
                $DEBUG && console.error("Unexpected coverage evaluation status: " + this.latestCoverageData.pipelineCoverageSummary.evaluationStatus);
                return false;
        }
    }

    private fetchCodeCoverageHTMLSummary(runViewArtifact: RunViewArtifact): Promise<string | null> {
        return getBuildClient(this.pageContext)
            .getArtifacts(runViewArtifact.projectId, runViewArtifact.buildId)
            .then(pipelineArtifacts => {
                return pipelineArtifacts && pipelineArtifacts.length > 0
                    ? this.getHTMLSummaryLinkFromArtifact(pipelineArtifacts, runViewArtifact.buildId)
                    : null;
            });
    }

    private getHTMLSummaryLinkFromArtifact(buildArtifacts: BuildArtifact[], buildId: number): string | null {
        const filteredArtifacts = buildArtifacts.filter((pipelineArtifact: BuildArtifact) =>
            equals(pipelineArtifact.name, format("Code Coverage Report_{0}", buildId))
        );

        if (
            filteredArtifacts.length >= 1 &&
            filteredArtifacts[0].resource &&
            filteredArtifacts[0].resource._links &&
            filteredArtifacts[0].resource._links.web &&
            filteredArtifacts[0].resource._links.web.href &&
            filteredArtifacts[0].resource._links.web.href.length &&
            filteredArtifacts[0].resource.data &&
            filteredArtifacts[0].resource.data.length
        ) {
            const tfsProject = getTFSProject(this.pageContext);

            if (tfsProject) {
                const collectionUrl = routeUrl(this.pageContext, "ms.vss-tfs-web.project-overview-route");
                const projectId = tfsProject.id;
                let containerPath = filteredArtifacts[0].resource.data;

                // Data format will be in this format (#/ContainerId/FolderPath)
                // Double check if that's the format and if not take precautions
                if (containerPath.charAt(0) === "#") {
                    containerPath = containerPath.substring(1);
                }
                if (containerPath.charAt(0) !== PathConstants.PathSeparator) {
                    containerPath = PathConstants.PathSeparator + containerPath;
                }

                // index.html is the one that should be retrieved
                // preferred to hardcode at client rather than at server
                let url =
                    collectionUrl.lastIndexOf(PathConstants.PathSeparator) === collectionUrl.length - 1
                        ? collectionUrl
                        : collectionUrl + PathConstants.PathSeparator;
                url = url + projectId + "/_apis/test/CodeCoverage/browse" + containerPath + "/index.html";

                return url;
            }
        }

        return null;
    }
}
